import { useSyncExternalStore } from 'react'

/**
 * The project's custom theme breakpoints (see `--breakpoint-*` in theme.css).
 * The shared `useMediaQuery` hook tracks the *default* Tailwind px (640/768/
 * 1024/…), which only coincides with this scale at 768 — so it can't express
 * `l` (980). Marquee's responsive `l:`/`m:` utility classes and its JS layout
 * switches must agree on the same px, otherwise a container capped at `l:max-h`
 * pairs with an un-scrolled mobile list in the 980–1024 gap (content overflows).
 */
const THEME_BREAKPOINTS = {
	s: 414,
	m: 768,
	l: 980,
	xl: 1200,
	xxl: 1440
} as const

export type ThemeBreakpoint = keyof typeof THEME_BREAKPOINTS

interface BreakpointStore {
	mediaQueryList: MediaQueryList
	subscribers: Set<() => void>
}

/**
 * One MediaQueryList and one `change` listener per breakpoint, shared by every
 * subscriber. The browser runs each listener as its own callback and drains
 * microtasks between them, which is where React flushes sync updates: with a
 * listener per component, a child (registered first, since child effects run
 * first) re-rendered on the new breakpoint while its parent was still on the
 * old one, and a `ResponsiveConfirmTrigger` found itself outside its dialog.
 * Fanning out from a single listener queues every update before the flush, so
 * all subscribers change in the same render. The listener is never removed:
 * one per breakpoint for the life of the app.
 */
const stores = new Map<ThemeBreakpoint, BreakpointStore>()

const getStore = (breakpoint: ThemeBreakpoint): BreakpointStore => {
	const existing = stores.get(breakpoint)
	if (existing) {
		return existing
	}
	const mediaQueryList = window.matchMedia(
		`(min-width: ${THEME_BREAKPOINTS[breakpoint]}px)`
	)
	const subscribers = new Set<() => void>()
	mediaQueryList.addEventListener('change', () => {
		for (const notify of subscribers) {
			notify()
		}
	})
	const store = { mediaQueryList, subscribers }
	stores.set(breakpoint, store)
	return store
}

/** True when the viewport is at least the given theme breakpoint wide. */
export const useMinWidth = (breakpoint: ThemeBreakpoint): boolean => {
	const store = getStore(breakpoint)
	return useSyncExternalStore(
		(onChange) => {
			store.subscribers.add(onChange)
			return () => store.subscribers.delete(onChange)
		},
		() => store.mediaQueryList.matches
	)
}
